import { axiosInstance } from "@/utils/axios";
import { useAuthStore } from "@/stores/auth";
import { getClientSessionId, getCurrentPagePath } from "@/utils/analytics";

let interceptorsConfigured = false;
const AUTH_BYPASS_PATHS = [
    '/api/v1/auth/login',
    '/api/v1/auth/register',
    '/api/v1/auth/forgot-password',
    '/api/v1/auth/reset-password',
    '/api/v1/auth/refresh',
    '/api/v1/auth/logout',
    '/api/v1/app-settings/public'
];

function ensureBearerToken(token = "") {
    if (!token) return "";
    return token.startsWith("Bearer ") ? token : `Bearer ${token}`;
}

function shouldBypassAuthHandling(url = '') {
    return AUTH_BYPASS_PATHS.some((path) => url.includes(path));
}

export default function useApi() {

    const authStore = useAuthStore();

    if (!interceptorsConfigured) {
        interceptorsConfigured = true;
        axiosInstance.interceptors.request.use(
            async (config) => {
                const url = config?.url || '';
                const bypassAuthHandling = shouldBypassAuthHandling(url);

                if (!bypassAuthHandling) {
                    await authStore.ensureValidAccessToken();
                }

                config.headers = config.headers || {};
                const clientSessionId = getClientSessionId();
                if (clientSessionId) {
                    config.headers['X-Client-Session-Id'] = clientSessionId;
                }
                const pagePath = getCurrentPagePath();
                if (pagePath) {
                    config.headers['X-Page-Path'] = pagePath;
                }
                const currentAuthorization = config.headers['Authorization'];
                if (!currentAuthorization && authStore.accessToken) {
                    config.headers['Authorization'] = ensureBearerToken(authStore.accessToken);
                } else if (typeof currentAuthorization === 'string' && !currentAuthorization.startsWith('Bearer ') && authStore.accessToken) {
                    config.headers['Authorization'] = ensureBearerToken(currentAuthorization);
                }
                return config
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        axiosInstance.interceptors.response.use(
            (response) => {
                return response;
            },
            async (error) => {
                const prevRequest = error?.config;
                const url = prevRequest?.url || '';
                const bypassAuthHandling = shouldBypassAuthHandling(url);
                const isUnauthorized = error?.response?.status === 403 || error?.response?.status === 401;
                const canRetry = Boolean(prevRequest) && isUnauthorized && !bypassAuthHandling && !prevRequest?.sent && Boolean(authStore.id || authStore.accessToken);

                if (!canRetry) {
                    return Promise.reject(error);
                }

                prevRequest.sent = true;

                try {
                    await authStore.refreshToken();
                    prevRequest.headers = prevRequest.headers || {};
                    prevRequest.headers['Authorization'] = ensureBearerToken(authStore.accessToken);
                    return axiosInstance(prevRequest);
                } catch (refreshError) {
                    return Promise.reject(refreshError);
                }
            }
        );
    }

    return axiosInstance
}
